/**
 * A card in a further language (S36): the view of a menu (`menuView`) with the dishes' translations,
 * either instead of the German („Karte auf Englisch“) or below it, in italics, as a second line
 * („zweisprachig“, usual in holiday places). The same for print, PDF and Word; a translation that is
 * missing leaves the German text. The card's own texts – title, sections, notes, courses, labels of
 * prices, texts of the catalogue, own tags and allergens – from the business's dictionary (`phrases.ts`);
 * on a bilingual card a short one as „Vorspeisen / Starters“.
 */
import { BOTH } from "./design.ts";
import type { MenuView, ViewItem, ViewVariant } from "./menu.ts";
import { AS_OF } from "@household-kit/lang";
import { CARD_WORDS, allergenName, dishIn, tagIn, translationState } from "./translation.ts";
import type { CardWords, Lang, Translatable } from "./translation.ts";
import { phraseIn, phraseMissing, viewPhrases } from "./phrases.ts";
import type { Phrases } from "./phrases.ts";

/** In which language a card is set: a further one alone, or German with it below. */
export interface CardLang {
  lang: Lang;
  both: boolean;
}

/** „Deutsch“, „Englisch“, „Deutsch und Englisch“: the choices before printing. */
export const cardLangsOf = (langs: readonly Lang[]): (CardLang | null)[] => [null, ...langs.flatMap((lang) => [{ lang, both: false }, { lang, both: true }])];
export const cardLangKey = (c: CardLang | null | undefined) => (c ? `${c.lang}${c.both ? "+de" : ""}` : "de");
export function cardLangOf(key: string, langs: readonly Lang[]): CardLang | null {
  const m = /^([a-z]{2})(\+de)?$/.exec(key);
  const lang = m && langs.find((l) => l === m[1]);
  return lang ? { lang, both: Boolean(m[2]) } : null;
}

/** What of a card is not yet in each language (S36): its dishes and drinks, its own texts. */
export function cardGaps(view: MenuView, dishes: ReadonlyMap<string, Translatable>, langs: readonly Lang[], phrases: Phrases = {}): { lang: Lang; dishes: number; texts: number }[] {
  const on = [...new Set(view.sections.flatMap((s) => s.items.filter((i) => !i.text).map((i) => i.dish)))].flatMap((id) => dishes.get(id) ?? []);
  const words = viewPhrases(view);
  return langs.map((lang) => ({ lang, dishes: on.filter((d) => translationState(d, lang) !== "done").length, texts: words.filter((t) => phraseMissing(t, lang, phrases)).length }));
}

/** The languages a card is wholly in (Martin, 01.11.2026): only they go on the net (`freezeForNet`). */
export const completeLangs = (view: MenuView, dishes: ReadonlyMap<string, Translatable>, langs: readonly Lang[], phrases: Phrases = {}): Lang[] =>
  cardGaps(view, dishes, langs, phrases).filter((g) => g.dishes + g.texts === 0).map((g) => g.lang);

/** The view in another language; German as it is without one. */
export function viewIn(view: MenuView, dishes: ReadonlyMap<string, Translatable>, to: CardLang | null | undefined, phrases: Phrases = {}): MenuView {
  if (!to) return view;
  const say = (s: string) => phraseIn(s, to.lang, phrases);
  // A short text of the card: the translation, or on a bilingual card both („Vorspeisen / Starters“).
  const both = (s: string) => {
    const t = say(s);
    return t === s ? s : to.both ? `${s}${BOTH}${t}` : t;
  };
  const opt = <T extends string | undefined>(s: T): T => (s ? both(s) : s) as T;
  const variant = (v: ViewVariant): ViewVariant => ({ ...v, label: both(v.label) });
  const tag = (t: string) => tagIn(t, to.lang, phrases);
  const item = (i: ViewItem): ViewItem => {
    // A column's head stays German on a bilingual card: it is narrow.
    const own = { note: both(i.note), priceLabel: both(i.priceLabel), variants: i.variants.map(variant), ...(i.cols ? { cols: to.both ? i.cols : i.cols.map(say) } : {}), ...(i.course ? { course: both(i.course), ...(to.both && say(i.course) !== i.course ? { courseLines: [i.course, say(i.course)] as [string, string] } : {}) } : {}), ...(i.sub ? { sub: both(i.sub) } : {}) };
    // A text of the catalogue (S14) as a dish: its title and text, on a bilingual card a line below.
    const d = i.text ? { name: i.name, description: i.description } : dishes.get(i.dish);
    if (!d) return { ...i, ...own };
    const t = i.text ? { name: say(i.name), description: say(i.description) } : dishIn(d, to.lang);
    // The diet's tags in the language (vegetarian, gluten-free); a bilingual card keeps the German ones.
    if (!to.both) return { ...i, ...own, name: t.name, description: t.description, tags: i.tags.map(tag) };
    // Below the German only what differs: a name kept as it is is not said twice.
    const line = [t.name !== i.name ? t.name : "", t.description !== i.description ? t.description : ""].filter(Boolean).join(" – ");
    return line ? { ...i, ...own, trans: line } : { ...i, ...own };
  };
  return {
    ...view,
    lang: to,
    title: both(view.title),
    subtitle: both(view.subtitle),
    note: both(view.note),
    sections: view.sections.map((s) => ({ ...s, title: both(s.title), note: both(s.note), items: s.items.map(item), ...(s.priceLabel ? { priceLabel: both(s.priceLabel) } : {}), ...(s.variants ? { variants: s.variants.map(variant) } : {}), ...(s.priceHead ? { priceHead: opt(s.priceHead) } : {}) })),
    // A tag's symbol (S21) under its name in the language.
    tagIcons: to.both ? view.tagIcons : Object.fromEntries(Object.entries(view.tagIcons).map(([t, ic]) => [tag(t), ic])),
    legend: view.legend.map((a) => {
      const name = allergenName(a, to.lang, phrases);
      return { ...a, name: to.both && name !== a.name ? `${a.name}${BOTH}${name}` : name };
    }),
  };
}

/** The words a card prints besides the business's own, in its language; both, joined, on a bilingual one. */
export function cardWords(view: Pick<MenuView, "lang">): CardWords & { asOf: string } {
  const de = { ...CARD_WORDS.de, asOf: "Stand" };
  const to = view.lang;
  if (!to) return de;
  const other = { ...CARD_WORDS[to.lang], asOf: AS_OF[to.lang] };
  if (!to.both) return other;
  return Object.fromEntries(Object.keys(de).map((k) => [k, de[k as keyof typeof de] === other[k as keyof typeof other] ? de[k as keyof typeof de] : `${de[k as keyof typeof de]}${BOTH}${other[k as keyof typeof other]}`])) as typeof de;
}

